/* ============================================================
   Fence Works — measured grade (elevation profile) helpers
   A side's `profile` is the surveyor-style shot list along a run:
     side.profile?: Array<{ t: number, deltaIn: number }>
       t       = 0..1 position along the run from a→b
       deltaIn = inches of elevation change relative to the PREVIOUS
                 point (the first point is relative to run-start
                 elevation 0; − = drop, + = rise)
       array kept sorted by t ascending.
   Pure helpers — shared by the estimator (capture UI), the pricing
   engine (lib/engine) and the quote schematic (quote.tsx SitePlan).
   ============================================================ */

export type GradePoint = { t: number; deltaIn: number };

// grade key → pricing factor — mirrors the estimator's GRADES map
// (level 0 · slope_* 0.15 · step_* 0.25)
export const GRADE_FACTORS: any = { level: 0, slope_to: 0.15, slope_away: 0.15, step_to: 0.25, step_away: 0.25 };

// slope % → tier. Single source of truth: auto-detect (lib/slope.ts,
// re-exported on window.VFP_SLOPE) and measured profiles share it.
export function tierFor(pct: any) {
  if (pct < 5) return { tier: "level", grade: "level", label: "Flat / level" };
  if (pct <= 15) return { tier: "moderate", grade: "slope_to", label: "Moderate grade" };
  return { tier: "steep", grade: "step_to", label: "Steep — stepped/racked" };
}

// cumulative elevation (inches) at each profile point, in t order
export function cumSeries(profile: any): Array<{ t: number; elevIn: number }> {
  let cum = 0;
  return (Array.isArray(profile) ? profile : []).map((p: any) => { cum += (+p.deltaIn || 0); return { t: +p.t || 0, elevIn: cum }; });
}

// interpolated cumulative elevation (inches) at position t along the run —
// piecewise-linear between points; before the first point = 0 (the run-start
// datum); past the last point = the last cumulative value.
export function cumElevIn(profile: any, t: number): number {
  const pts = cumSeries(profile);
  if (!pts.length) return 0;
  if (t < pts[0].t) return 0;
  for (let i = 1; i < pts.length; i++) {
    if (t <= pts[i].t) {
      const a = pts[i - 1], b = pts[i], span = b.t - a.t;
      return span > 1e-9 ? a.elevIn + (b.elevIn - a.elevIn) * ((t - a.t) / span) : b.elevIn;
    }
  }
  return pts[pts.length - 1].elevIn;
}

// the run's overall measured grade %: (maxCum − minCum) inches (the run-start
// datum 0 counts, matching cumElevIn) / (lfFt·12) · 100.
// 0 if fewer than 2 points or no usable span.
export function runGradePct(profile: any, lfFt: number): number {
  if (!Array.isArray(profile) || profile.length < 2) return 0;
  const runIn = (+lfFt || 0) * 12;
  if (runIn <= 0) return 0;
  let cum = 0, mn = 0, mx = 0;
  profile.forEach((p: any) => { cum += (+p.deltaIn || 0); if (cum < mn) mn = cum; if (cum > mx) mx = cum; });
  return ((mx - mn) / runIn) * 100;
}

// true when a side carries enough measured points to price from
export function hasProfile(side: any): boolean {
  return !!(side && Array.isArray(side.profile) && side.profile.length >= 2);
}

// measured-grade summary for a run: % + tier + the pricing factor
export function measuredGrade(profile: any, lfFt: any) {
  const pct = runGradePct(profile, lfFt);
  const t = tierFor(pct);
  return { pct, ...t, factor: GRADE_FACTORS[t.grade] || 0 };
}

/* ============================================================
   Absolute corner elevations — the contractor's post-height model.
     parcel.cornerElev?: { [cornerIndex]: inches }
   Sparse, signed inches; 0 = wherever the user set their base.
   Corner i is polygon[i]; side i runs corner i → corner (i+1)%nC.
   `runElevSamples` is the single source of truth the 3D viewer,
   the 2D site plan and (opt-in) pricing all consume: a baseline
   linear slope between the run's two corner elevations, with the
   run's own `profile` (relative drop/rise shots) detrended and
   laid on top as intermediate bumps.
   ============================================================ */

// true when the parcel carries ANY measured grade — a surveyed run profile
// or an absolute corner elevation. Gates the chained-hill rendering and the
// 3D viewer's Slope-on default; false keeps the legacy flat behavior.
export function parcelHasGrade(parcel: any): boolean {
  if (!parcel) return false;
  const ce = parcel.cornerElev;
  if (ce && typeof ce === "object" && Object.keys(ce).some((k: any) => isFinite(+ce[k]))) return true;
  return Array.isArray(parcel.sides) && parcel.sides.some((s: any) => hasProfile(s));
}

// a side's profile usable for the corner chain: ≥1 shot is enough here —
// the corners supply the datum that the old standalone ≥2 rule guaranteed
const profPts = (side: any) => (side && Array.isArray(side.profile) && side.profile.length ? side.profile : null);

// every corner's absolute elevation (inches vs base 0), or null when the
// parcel has no measured grade. Explicit cornerElev values always win;
// profiled runs chain their net drop/rise into unset neighbor corners; a
// profiled run not yet connected to any set corner is anchored flat at the
// nearest set corner (or the 0 datum when nothing is set — reproducing the
// old run-start datum for legacy pure-profile parcels); any corners still
// unset interpolate between the nearest set corners along the perimeter
// (footage-weighted) — open-chain ends extend flat from the nearest set
// corner. The result is ONE elevation per corner, so runs sharing a corner
// always chain continuously.
export function cornerElevations(parcel: any): number[] | null {
  if (!parcel || !Array.isArray(parcel.sides) || !parcel.sides.length) return null;
  const sides = parcel.sides;
  const nS = sides.length;
  const nC = Array.isArray(parcel.polygon) && parcel.polygon.length ? parcel.polygon.length
    : (parcel.closed !== false ? nS : nS + 1);
  if (!(nC > 0)) return null;
  const closed = parcel.closed !== false && nS === nC;
  const ce = (parcel.cornerElev && typeof parcel.cornerElev === "object") ? parcel.cornerElev : {};
  const E: any[] = new Array(nC).fill(null);
  let anyExplicit = false;
  Object.keys(ce).forEach((k: any) => {
    const i = Math.round(+k), v = +ce[k];
    if (isFinite(i) && i >= 0 && i < nC && isFinite(v)) { E[i] = v; anyExplicit = true; }
  });
  // net drop/rise across each profiled side
  const nets: any[] = new Array(nS).fill(null);
  sides.forEach((s: any) => { if (s && profPts(s) && s.i >= 0 && s.i < nS) nets[s.i] = cumElevIn(s.profile, 1); });
  if (!anyExplicit && !nets.some((v: any) => v != null)) return null;     // flat parcel — no measured grade
  // nearest set corner to `from` (backward first — "run K starts where K−1
  // ended"), 0 when nothing is set anywhere
  const findNear = (from: number) => {
    for (let d = 1; d < nC; d++) {
      const back = closed ? (from - d + nC * nC) % nC : from - d;
      if (back >= 0 && back < nC && E[back] != null) return E[back];
      const fwd = closed ? (from + d) % nC : from + d;
      if (fwd >= 0 && fwd < nC && E[fwd] != null) return E[fwd];
    }
    return 0;
  };
  // chain profile nets into unset corners (explicit corners are never
  // overwritten); anchor each disconnected profiled cluster, then re-chain
  for (let guard = 0; guard <= nS; guard++) {
    for (let pass = 0; pass <= nC; pass++) {
      let moved = false;
      for (let i = 0; i < nS; i++) {
        if (nets[i] == null) continue;
        const a = i, b = (i + 1) % nC;
        if (E[a] != null && E[b] == null) { E[b] = E[a] + nets[i]; moved = true; }
        else if (E[b] != null && E[a] == null) { E[a] = E[b] - nets[i]; moved = true; }
      }
      if (!moved) break;
    }
    const orphan = nets.findIndex((v: any, i: number) => v != null && E[i] == null && E[(i + 1) % nC] == null);
    if (orphan < 0) break;
    E[orphan] = findNear(orphan);   // flat carry from the nearest set corner
  }
  const known: number[] = [];
  for (let i = 0; i < nC; i++) if (E[i] != null) known.push(i);
  const lfOf = (i: number) => { const s = sides[i]; const v = s ? +s.lf : NaN; return isFinite(v) && v > 0 ? v : 1; };
  if (known.length === 1) {
    for (let i = 0; i < nC; i++) E[i] = E[known[0]];                      // one datum → flat at it
  } else if (closed) {
    for (let g = 0; g < known.length; g++) {                              // fill each cyclic gap between set corners
      const ka = known[g], kb = known[(g + 1) % known.length];
      const span = (kb - ka + nC) % nC;
      if (span <= 1) continue;
      let total = 0;
      for (let s = 0; s < span; s++) total += lfOf((ka + s) % nC);
      let acc = 0;
      for (let s = 1; s < span; s++) {
        acc += lfOf((ka + s - 1) % nC);
        const idx = (ka + s) % nC;
        if (E[idx] == null) E[idx] = E[ka] + (E[kb] - E[ka]) * (total > 0 ? acc / total : s / span);
      }
    }
  } else {
    const first = known[0], last = known[known.length - 1];
    for (let i = 0; i < first; i++) E[i] = E[first];                      // open ends sit flat at the nearest set corner
    for (let i = last + 1; i < nC; i++) E[i] = E[last];
    for (let g = 0; g < known.length - 1; g++) {
      const ka = known[g], kb = known[g + 1];
      let total = 0;
      for (let s = ka; s < kb; s++) total += lfOf(s);
      let acc = 0;
      for (let i = ka + 1; i < kb; i++) {
        acc += lfOf(i - 1);
        if (E[i] == null) E[i] = E[ka] + (E[kb] - E[ka]) * (total > 0 ? acc / total : (i - ka) / (kb - ka));
      }
    }
  }
  return E.map((v: any) => (isFinite(+v) ? +v : 0));
}

// one run's absolute elevation samples [{ t: 0..1, elevIn }], sorted by t —
// piecewise-linear between samples. Baseline = linear cornerElev[i] →
// cornerElev[i+1]; the run's profile is detrended (its end-to-end fall is the
// corners' job once they're set) and added as intermediate bumps. With no
// explicit corners the chain derives the corners FROM the profiles, so a
// legacy profile-only run reproduces its surveyed shape exactly. Returns
// null when the parcel has no measured grade (legacy flat behavior).
export function runElevSamples(parcel: any, sideIndex: number): Array<{ t: number; elevIn: number }> | null {
  const E = cornerElevations(parcel);
  if (!E) return null;
  const nC = E.length;
  if (!(sideIndex >= 0) || sideIndex >= nC) return null;
  const side = parcel.sides.find((s: any) => s && s.i === sideIndex);
  if (!side) return null;
  const e0 = E[sideIndex], e1 = E[(sideIndex + 1) % nC];
  if (!hasProfile(side)) return [{ t: 0, elevIn: e0 }, { t: 1, elevIn: e1 }];
  const net = cumElevIn(side.profile, 1);
  const out: Array<{ t: number; elevIn: number }> = [{ t: 0, elevIn: e0 }];
  cumSeries(side.profile).forEach((p: any) => {
    const t = Math.max(0, Math.min(1, +p.t || 0));
    if (t <= 1e-6 || t >= 1 - 1e-6) return;   // endpoints are owned by the corners
    out.push({ t, elevIn: e0 + (e1 - e0) * t + (p.elevIn - net * t) });
  });
  out.push({ t: 1, elevIn: e1 });
  return out.sort((a, b) => a.t - b.t);
}

// piecewise-linear elevation (inches) at t along a run's samples
export function sampleElevAt(samples: any, t: number): number {
  const pts = Array.isArray(samples) ? samples : [];
  if (!pts.length) return 0;
  if (t <= pts[0].t) return +pts[0].elevIn || 0;
  for (let i = 1; i < pts.length; i++) {
    if (t <= pts[i].t) {
      const a = pts[i - 1], b = pts[i], span = b.t - a.t;
      return span > 1e-9 ? (+a.elevIn || 0) + ((+b.elevIn || 0) - (+a.elevIn || 0)) * ((t - a.t) / span) : (+b.elevIn || 0);
    }
  }
  return +pts[pts.length - 1].elevIn || 0;
}

// measured grade % of a sampled run: (max − min) inches / (lfFt·12) · 100 —
// the samples-based twin of runGradePct
export function samplesGradePct(samples: any, lfFt: any): number {
  if (!Array.isArray(samples) || samples.length < 2) return 0;
  const runIn = (+lfFt || 0) * 12;
  if (runIn <= 0) return 0;
  let mn = Infinity, mx = -Infinity;
  samples.forEach((p: any) => { const v = +p.elevIn || 0; if (v < mn) mn = v; if (v > mx) mx = v; });
  return mx > mn ? ((mx - mn) / runIn) * 100 : 0;
}
